import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import { SplitSupplyNoticeValueSource } from '@ebalance/proto/bffuser/v1/split_supply_plan_model_pb'
import { useTranslation } from 'react-i18next'

import {
  Box,
  Input,
  LightModeSemanticColors,
} from '@enechain/ecloud-designsystem'

import {
  TABLE_BODY_CELL_STYLE,
  TABLE_HEADER_BG,
  TABLE_HEADER_CELL_STYLE,
} from '~/components/common/MasterDataTable'
import { KOMA_COUNT } from '~/constants/koma'
import {
  type GridCellUpdate,
  useExcelGridEditing,
} from '~/hooks/useExcelGridEditing'
import {
  type CellPos,
  isInRange,
  isMultiCellRange,
  parseTsv,
  planPaste,
} from '~/models/gridSelection'
import { komaNumberTimeRange } from '~/models/komaToTime'
import { parseSlotValue } from '~/models/splitSupplySlot'
import {
  type NoticeDraft,
  type NoticeGridPoint,
  dayCountOf,
  isBlankSource,
  rowTotal,
} from '~/pages/SplitSupplyPlanDetail/models/draft'

const NS = 'page:splitSupplyPlanDetail.grid' as const
const LABEL_COL = 0
const FIRST_POINT_COL = 1
// 列幅は画面幅に合わせて広げず固定する (需要調達計画の表 `components/planSubmit/PlanTable` と同じ作り)。
// 地点の列は 22 桁の供給地点特定番号、 見出しの列は「ベース分契約識別番号 1」が切れずに入る幅
const POINT_COL_WIDTH = 184
const TIME_COL_WIDTH = 200
const ROW_OVERSCAN = 4
const COLUMN_OVERSCAN = 2
const INITIAL_ROW_HEIGHT = 34
const INITIAL_HEADER_HEIGHT = 252
const INITIAL_VIEW_WIDTH = 1024
const INITIAL_VIEW_HEIGHT = 600
const SMALL_ROW_COUNT = 64
const MAX_UNWINDOWED_CELLS = 5000
const LEFT_SPACER = -1
const RIGHT_SPACER = -2

// 生 table なので、 色は chakra の CSS 変数でなく token の値で当てる (MasterDataTable と同じ)
const COLORS = {
  gridLine: LightModeSemanticColors.Border.lowEmphasis.value,
  headerDivider: LightModeSemanticColors.Border.midEmphasis.value,
  timeBg: LightModeSemanticColors.Surface.primary.value,
  // 選択は React mock の ExcelGrid と同じ: 範囲の面を薄く塗り、 外周だけを太い枠で囲む
  selectionFill: LightModeSemanticColors.Surface.accentPrimaryLightLow.value,
  selectionFrame: LightModeSemanticColors.Border.accentPrimary.value,
  // 空欄 (値が決まらないコマ) は mock の `.cell-blank` (--surface-negative-low) と同じ
  blank: LightModeSemanticColors.Surface.negativeLow.value,
  invalidText: LightModeSemanticColors.Text.negativeMid.value,
  invalidLine: LightModeSemanticColors.Border.negativeMid.value,
} as const
// Excel のように全セルに縦線と横線を引く。 見出し・時刻の列と値の境目は少し濃く
const cellLines = (
  headerRowCount: number,
  row: number,
  col: number,
): React.CSSProperties => ({
  borderRight: `1px solid ${col === LABEL_COL ? COLORS.headerDivider : COLORS.gridLine}`,
  borderBottom: `1px solid ${row === headerRowCount - 1 ? COLORS.headerDivider : COLORS.gridLine}`,
})

type HeaderKey =
  | 'number'
  | 'customerId'
  | 'customer'
  | 'base'
  | 'contractId'
  | 'priority'
  | 'total'
/** 見出し: 地点の番号・需要家識別番号・需要家名・ベース分供給者・契約識別番号 1・優先順位・合計 (自社・他社の通告値で同じ)。 */
const HEADER_KEYS: readonly HeaderKey[] = [
  'number',
  // 需要家識別番号は mock と同じ位置に出す。 本番の顧客管理に項目が無いため全地点「—」(XML には出さない)
  'customerId',
  'customer',
  'base',
  'contractId',
  'priority',
  'total',
]

type Props = {
  points: readonly NoticeGridPoint[]
  /** 編集中の値 (地点ごとに 48 コマの文字列)。 */
  draft: NoticeDraft
  /** 編集モードか。 見ているだけの時も選択・コピーはできるが、 値は変えられない。 */
  isEditing: boolean
  onChange: (next: NoticeDraft) => void
}

const pad2 = (n: number): string => String(n).padStart(2, '0')
/** 地点の、 draft の位置のコマ (日をまたいで 48 コマずつ)。 */
type GridSlot = NoticeGridPoint['days'][number]['slots'][number]
const slotAt = (p: NoticeGridPoint, index: number): GridSlot | undefined =>
  p.days[Math.floor(index / KOMA_COUNT)]?.slots[index % KOMA_COUNT]
/** 値のセルの正しい数字を 3 桁区切りにする (日量・月間合計の表記と揃える)。 時刻の列と正しくない入力はそのまま。 */
const groupedText = (text: string, col: number): string => {
  if (col === LABEL_COL) return text
  const n = parseSlotValue(text)
  return n === undefined ? text : n.toLocaleString('en-US')
}

/**
 * 通の 48 コマの表 (列 = 供給地点、 行 = 30 分のコマ)。 見出しは供給地点の番号・需要家名・
 * ベース分供給者・契約識別番号 1・優先順位と、 その下に日量。
 * 月間の通の「月全体」表示では、 行が 日付 × 時刻 (対象月の全日 × 48 コマ) になり、 行見出しに日付と
 * 時刻が出る。 見出しの合計は月間の合計になる。 範囲選択・コピー・貼り付けは日をまたいでそのまま効く。
 *
 * Excel 風の操作 (SlotValueGrid と同じ): クリック / ドラッグで範囲選択・矢印キーで移動
 * (Shift で範囲拡張)・Ctrl+C で選んだ範囲だけを TSV コピー。 見出しと時刻の列もただのセルで、
 * 選んだ時だけコピーに入る (自動では付けない)。 編集モードでは F2 / Enter / 文字入力で編集開始・
 * Enter で確定して下へ・Esc で取消・Ctrl+V で選択セルから貼り付け (縦横とも)・Delete で選択範囲を
 * 空にする (どれも 48 コマの値のセルだけに効く)。 日本語 IME の変換中の Enter / Esc はセル操作にしない。
 *
 * 空欄 (値が入っていないコマ。 作成した時に基本パターンが当たらなかったコマも含む) は色で示す。
 * 値の有無は hover で見える。
 */
export const NoticeValueGrid = ({
  points,
  draft,
  isEditing,
  onChange,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const headerKeys = HEADER_KEYS
  /** 表の行は「見出しの行 (headerKeys) → 48 コマ」の順の 1 本の並び。 見出しもただのセルとして選べる。 */
  const headerRowCount = headerKeys.length
  /** 表の行から、 draft の中の位置 (日の順に 48 コマずつ並べた何番目か) へ。 */
  const slotOf = (row: number): number => row - headerRowCount
  const isSlotRow = (row: number): boolean => row >= headerRowCount
  /** 値を変えられるのは 48 コマの地点のセルだけ (見出し・時刻の列は選択とコピーだけ)。 */
  const isValueCell = useCallback(
    (row: number, col: number): boolean =>
      row >= headerRowCount && col >= FIRST_POINT_COL,
    [headerRowCount],
  )
  const dayCount = dayCountOf(points)
  const slotRowCount = dayCount * KOMA_COUNT
  const showDate = dayCount > 1
  /** 行見出しの文字: 1 日表示は時間帯 (HH:mm〜HH:mm)、 月全体は「月/日 時間帯」。 */
  const rowTimeLabel = (index: number): string => {
    const time = komaNumberTimeRange((index % KOMA_COUNT) + 1, '〜')
    const date = points[0]?.days[Math.floor(index / KOMA_COUNT)]?.deliveryDate
    return !showDate || date === undefined
      ? time
      : `${pad2(date.month)}/${pad2(date.day)} ${time}`
  }
  const firstValueCell: CellPos = {
    row: headerRowCount,
    col: FIRST_POINT_COL,
  }
  const bounds = {
    rowCount: headerRowCount + slotRowCount,
    colCount: points.length + 1,
  }

  const headerLabel = (key: HeaderKey): string => {
    switch (key) {
      // 地点の列見出しの行。 左上の角は 48 コマの行見出しの列の見出し
      case 'number':
        return t(`${NS}.row.timeRange`)
      case 'customerId':
        return t(`${NS}.row.customerIdentificationNumber`)
      case 'customer':
        return t(`${NS}.row.customerName`)
      case 'base':
        return t(`${NS}.row.baseSupplier`)
      case 'contractId':
        return t(`${NS}.row.baseContractId`)
      case 'priority':
        return t(`${NS}.row.priority`)
      case 'total':
        return t(showDate ? `${NS}.row.monthTotal` : `${NS}.row.dailyTotal`)
    }
  }

  const headerText = (key: HeaderKey, pointIndex: number): string => {
    const p = points[pointIndex]
    if (p === undefined) return ''
    switch (key) {
      case 'number':
        return p.supplyPointSpecificNumber
      case 'customerId':
        return '—'
      case 'customer':
        return p.customerName === '' ? '—' : p.customerName
      case 'base':
        return p.base === undefined
          ? '—'
          : `${p.base.businessCode} ${p.base.businessName}`
      case 'contractId':
        return p.baseContractId ?? '—'
      case 'priority':
        return p.basePriority === undefined ? '—' : String(p.basePriority)
      case 'total':
        return rowTotal(draft[pointIndex] ?? []).toLocaleString('en-US')
    }
  }

  /** セルに見える文字 (コピーもこの文字を写す)。 */
  const cellText = (row: number, col: number): string => {
    if (!isSlotRow(row)) {
      const key = headerKeys[row]
      if (key === undefined) return ''
      return col === LABEL_COL
        ? headerLabel(key)
        : headerText(key, col - FIRST_POINT_COL)
    }
    return col === LABEL_COL
      ? rowTimeLabel(slotOf(row))
      : (draft[col - FIRST_POINT_COL]?.[slotOf(row)] ?? '')
  }

  const grid = useExcelGridEditing({
    bounds,
    initialCell: firstValueCell,
    isEditable: isEditing,
    isValueCell,
    valueAt: (row, col) => draft[col - FIRST_POINT_COL]?.[slotOf(row)] ?? '',
    cellText,
    onCellsChange: (updates) => {
      const next = draft.map((row) => [...row])
      for (const { row, col, value } of updates) {
        const point = next[col - FIRST_POINT_COL]
        const slot = slotOf(row)
        if (point !== undefined && slot >= 0 && slot < point.length)
          point[slot] = value
      }
      onChange(next)
    },
  })
  const { rootRef, range, focus, editing } = grid

  // 編集モードに入ったら表に focus を置く。 キー入力は表 (grid) の onKeyDown が受けるので、 focus が
  // 「編集」 ボタンや body に残っていると、 最初から選ばれているセルへ打っても入らない
  // (「追加」 で作った直後に編集モードで開く時も、 「編集」 を押した直後も同じ)
  useEffect(() => {
    if (isEditing) rootRef.current?.focus({ preventScroll: true })
  }, [isEditing, rootRef])

  // 実際の行高を測ることで、フォントや表示倍率にも追従する。
  const [viewport, setViewport] = useState({
    left: 0,
    top: 0,
    width: INITIAL_VIEW_WIDTH,
    height: INITIAL_VIEW_HEIGHT,
    rowHeight: INITIAL_ROW_HEIGHT,
    headerHeight: INITIAL_HEADER_HEIGHT,
  })
  const previousFocus = useRef(focus)
  const previousEditing = useRef<CellPos | null>(null)
  const previousSize = useRef({
    width: viewport.width,
    height: viewport.height,
  })
  const measureViewport = useCallback((): void => {
    const root = rootRef.current
    if (root === null) return
    const rowHeight = root
      .querySelector('tr[data-slot-row]')
      ?.getBoundingClientRect().height
    const headerHeight = root
      .querySelector('thead')
      ?.getBoundingClientRect().height
    setViewport((previous) => {
      const next = {
        left: root.scrollLeft,
        top: root.scrollTop,
        width: root.clientWidth || INITIAL_VIEW_WIDTH,
        height: root.clientHeight || INITIAL_VIEW_HEIGHT,
        rowHeight: rowHeight || previous.rowHeight,
        headerHeight: headerHeight || previous.headerHeight,
      }
      return Object.keys(next).every(
        (key) =>
          next[key as keyof typeof next] ===
          previous[key as keyof typeof previous],
      )
        ? previous
        : next
    })
  }, [rootRef])
  useLayoutEffect(() => {
    const root = rootRef.current
    if (root === null) return
    measureViewport()
    const observer = new ResizeObserver(measureViewport)
    observer.observe(root)
    const header = root.querySelector('thead')
    const row = root.querySelector('tr[data-slot-row]')
    if (header !== null) observer.observe(header)
    if (row !== null) observer.observe(row)
    return () => {
      observer.disconnect()
    }
  }, [measureViewport, rootRef])

  // キーボード移動・編集開始・編集中のリサイズでは対象セルを表示する。
  // 手動スクロールだけでは最後の選択セルへ引き戻さない。
  const focusRow = focus.row
  const focusCol = focus.col
  const editingRow = editing?.row
  const editingCol = editing?.col
  useLayoutEffect(() => {
    const root = rootRef.current
    const old = previousFocus.current
    const oldEditing = previousEditing.current
    const oldSize = previousSize.current
    previousFocus.current = { row: focusRow, col: focusCol }
    previousEditing.current =
      editingRow === undefined || editingCol === undefined
        ? null
        : { row: editingRow, col: editingCol }
    previousSize.current = { width: viewport.width, height: viewport.height }
    const editingStarted =
      editingRow !== undefined &&
      editingCol !== undefined &&
      (oldEditing?.row !== editingRow || oldEditing.col !== editingCol)
    const editingResized =
      editingRow !== undefined &&
      editingCol !== undefined &&
      (oldSize.width !== viewport.width || oldSize.height !== viewport.height)
    const focusMoved = old.row !== focusRow || old.col !== focusCol
    if (root === null || (!focusMoved && !editingStarted && !editingResized))
      return
    const targetRow = editingStarted || editingResized ? editingRow : focusRow
    const targetCol = editingStarted || editingResized ? editingCol : focusCol
    if (targetCol > 0 && root.clientWidth > 0) {
      const left = TIME_COL_WIDTH + (targetCol - 1) * POINT_COL_WIDTH
      if (left < root.scrollLeft + TIME_COL_WIDTH)
        root.scrollLeft = left - TIME_COL_WIDTH
      else if (left + POINT_COL_WIDTH > root.scrollLeft + root.clientWidth)
        root.scrollLeft = left + POINT_COL_WIDTH - root.clientWidth
    }
    if (targetRow >= headerRowCount && root.clientHeight > 0) {
      const top =
        viewport.headerHeight +
        (targetRow - headerRowCount) * viewport.rowHeight
      if (top < root.scrollTop + viewport.headerHeight)
        root.scrollTop = top - viewport.headerHeight
      else if (top + viewport.rowHeight > root.scrollTop + root.clientHeight)
        root.scrollTop = top + viewport.rowHeight - root.clientHeight
    }
    measureViewport()
  }, [
    focusRow,
    focusCol,
    editingRow,
    editingCol,
    measureViewport,
    rootRef,
    viewport.width,
    viewport.height,
    viewport.headerHeight,
    viewport.rowHeight,
  ])

  const windowRows =
    slotRowCount > SMALL_ROW_COUNT &&
    slotRowCount * points.length > MAX_UNWINDOWED_CELLS
  const visibleRowStart = !windowRows
    ? 0
    : Math.min(
        slotRowCount,
        Math.max(
          0,
          Math.floor(viewport.top / viewport.rowHeight) - ROW_OVERSCAN,
        ),
      )
  const visibleRowEnd = !windowRows
    ? slotRowCount
    : Math.min(
        slotRowCount,
        Math.ceil(
          (viewport.top +
            Math.max(0, viewport.height - viewport.headerHeight)) /
            viewport.rowHeight,
        ) + ROW_OVERSCAN,
      )
  const visibleColStart = Math.min(
    points.length,
    Math.max(0, Math.floor(viewport.left / POINT_COL_WIDTH) - COLUMN_OVERSCAN),
  )
  const visibleColEnd = Math.min(
    points.length,
    Math.ceil(
      (viewport.left + Math.max(0, viewport.width - TIME_COL_WIDTH)) /
        POINT_COL_WIDTH,
    ) + COLUMN_OVERSCAN,
  )
  // 編集対象が画面外なら、途中の全セルを描画せず対象の周囲へ窓を移す。
  // layout effect で実際の scroll 位置も追いつかせる。
  const editSlot = editing === null ? undefined : slotOf(editing.row)
  const editPoint = editing === null ? undefined : editing.col - FIRST_POINT_COL
  const rowOutside =
    editSlot !== undefined &&
    (editSlot < visibleRowStart || editSlot >= visibleRowEnd)
  const colOutside =
    editPoint !== undefined &&
    (editPoint < visibleColStart || editPoint >= visibleColEnd)
  const rowStart = rowOutside
    ? Math.max(0, editSlot - ROW_OVERSCAN)
    : visibleRowStart
  const rowEnd = rowOutside
    ? Math.min(
        slotRowCount,
        editSlot +
          Math.ceil(
            Math.max(0, viewport.height - viewport.headerHeight) /
              viewport.rowHeight,
          ) +
          ROW_OVERSCAN,
      )
    : visibleRowEnd
  const colStart = colOutside
    ? Math.max(0, editPoint - COLUMN_OVERSCAN)
    : visibleColStart
  const colEnd = colOutside
    ? Math.min(
        points.length,
        editPoint +
          Math.ceil(
            Math.max(0, viewport.width - TIME_COL_WIDTH) / POINT_COL_WIDTH,
          ) +
          COLUMN_OVERSCAN,
      )
    : visibleColEnd
  const leftWidth = colStart * POINT_COL_WIDTH
  const rightWidth = (points.length - colEnd) * POINT_COL_WIDTH
  const visiblePointColumns = Array.from(
    { length: Math.max(0, colEnd - colStart) },
    (_, i) => colStart + i + FIRST_POINT_COL,
  )
  const allCols = [
    LABEL_COL,
    ...(leftWidth > 0 ? [LEFT_SPACER] : []),
    ...visiblePointColumns,
    ...(rightWidth > 0 ? [RIGHT_SPACER] : []),
  ]

  const handleScroll = (): void => {
    // 編集中セルをスクロールで DOM から外す前に、一時 draft に確定する。
    const root = rootRef.current
    if (root === null) return
    const firstColumn = Math.max(
      0,
      Math.floor(root.scrollLeft / POINT_COL_WIDTH) - COLUMN_OVERSCAN,
    )
    const lastColumn =
      Math.ceil(
        (root.scrollLeft + Math.max(0, root.clientWidth - TIME_COL_WIDTH)) /
          POINT_COL_WIDTH,
      ) + COLUMN_OVERSCAN
    const firstRow = windowRows
      ? Math.max(
          0,
          Math.floor(root.scrollTop / viewport.rowHeight) - ROW_OVERSCAN,
        )
      : 0
    const lastRow = windowRows
      ? Math.ceil(
          (root.scrollTop +
            Math.max(0, root.clientHeight - viewport.headerHeight)) /
            viewport.rowHeight,
        ) + ROW_OVERSCAN
      : slotRowCount
    if (
      editing !== null &&
      (editing.col - FIRST_POINT_COL < firstColumn ||
        editing.col - FIRST_POINT_COL >= lastColumn ||
        slotOf(editing.row) < firstRow ||
        slotOf(editing.row) >= lastRow) &&
      document.activeElement instanceof HTMLInputElement &&
      root.contains(document.activeElement)
    ) {
      document.activeElement.blur()
    }
    measureViewport()
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>): void => {
    if (editing !== null || !isEditing) return
    e.preventDefault()
    const matrix = parseTsv(e.clipboardData.getData('text'))
    // 貼り付けは値のセルから始める (見出しや時刻の列を選んでいても、 そこには書かない)
    const start: CellPos = {
      row: Math.max(range.rowMin, headerRowCount),
      col: Math.max(range.colMin, FIRST_POINT_COL),
    }
    const plan = planPaste(
      matrix,
      start,
      isMultiCellRange(range) ? range : null,
      bounds,
    )
    const updates: GridCellUpdate[] = []
    for (const { row, col } of grid.valueCellsIn(plan.targetRange)) {
      const value = plan.sourceAt(row, col)
      if (value !== undefined) updates.push({ row, col, value })
    }
    grid.setCells(updates)
    grid.setAnchor(start)
    grid.setFocus({
      row: plan.targetRange.rowMax,
      col: plan.targetRange.colMax,
    })
  }

  const cellHint = (
    p: NoticeGridPoint,
    slot: number,
    value: string,
  ): string | undefined => {
    const s = slotAt(p, slot)
    if (s === undefined) return undefined
    const original = s.value === undefined ? '' : String(s.value)
    if (value !== original) return t(`${NS}.hint.edited`)
    switch (s.source) {
      case SplitSupplyNoticeValueSource.SAVED:
        return t(`${NS}.hint.saved`)
      case SplitSupplyNoticeValueSource.BLANK:
        return t(`${NS}.hint.blank`)
      default:
        return undefined
    }
  }

  /** 見出しも値も同じ操作で選べるようにする、 セル共通の選択の配線。 */
  const selectionProps = (
    row: number,
    col: number,
    selected: boolean,
    isFocus: boolean,
  ): React.HTMLAttributes<HTMLTableCellElement> & {
    'data-focused'?: boolean
  } => ({
    'aria-selected': selected,
    'data-focused': isFocus || undefined,
    ...grid.cellMouseHandlers(row, col),
  })

  const multi = isMultiCellRange(range)
  /**
   * 選択の見た目 (React mock の ExcelGrid と同じ)。 範囲は面を薄く塗り (下地の空欄の色や見出しの地は
   * 透ける)、 外周の辺だけに太い枠を引く。 1 セルだけの選択と、 範囲の中のアクティブセルは塗らない。
   */
  const selectionStyle = (
    row: number,
    col: number,
    selected: boolean,
    isFocus: boolean,
  ): React.CSSProperties => {
    const base: React.CSSProperties = { cursor: 'cell', userSelect: 'none' }
    if (!selected) return base
    const c = COLORS.selectionFrame
    const shadows = [
      isInRange(range, row - 1, col) ? undefined : `inset 0 2px 0 ${c}`,
      isInRange(range, row, col + 1) ? undefined : `inset -2px 0 0 ${c}`,
      isInRange(range, row + 1, col) ? undefined : `inset 0 -2px 0 ${c}`,
      isInRange(range, row, col - 1) ? undefined : `inset 2px 0 0 ${c}`,
    ].filter((v): v is string => v !== undefined)
    const fill = `color-mix(in srgb, ${COLORS.selectionFill} 70%, transparent)`
    return {
      ...base,
      ...(shadows.length > 0 ? { boxShadow: shadows.join(', ') } : {}),
      ...(multi && !isFocus
        ? { backgroundImage: `linear-gradient(${fill}, ${fill})` }
        : {}),
    }
  }

  return (
    <Box
      ref={rootRef}
      flex="1"
      overflow="auto"
      minHeight="0"
      borderWidth="1px"
      borderColor="Border.lowEmphasis"
      borderRadius="md"
      // 表全体の focus の輪は出さない (React mock の ExcelGrid と同じ)。 どこにいるかはセルの選択枠で示す
      _focusVisible={{ outline: 'none' }}
      outline="none"
      aria-colcount={points.length + 1}
      aria-label={t(`${NS}.aria`)}
      aria-rowcount={headerRowCount + slotRowCount}
      onCopy={grid.handleCopy}
      onKeyDown={grid.handleKeyDown}
      onPaste={handlePaste}
      onScroll={handleScroll}
      role="grid"
      tabIndex={0}
    >
      <table
        style={{
          // table-layout: fixed は表の幅が確定していないと列の指定幅が効かないので、
          // 表の幅を「各列の指定幅の合計」にする (PlanTable と同じ)
          width: TIME_COL_WIDTH + points.length * POINT_COL_WIDTH,
          tableLayout: 'fixed',
          borderCollapse: 'separate',
          borderSpacing: 0,
        }}
      >
        <colgroup>
          <col style={{ width: TIME_COL_WIDTH }} />
          {leftWidth > 0 && <col style={{ width: leftWidth }} />}
          {visiblePointColumns.map((col) => (
            <col key={col} style={{ width: POINT_COL_WIDTH }} />
          ))}
          {rightWidth > 0 && <col style={{ width: rightWidth }} />}
        </colgroup>
        <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
          {headerKeys.map((key, row) => (
            <tr key={key} aria-rowindex={row + 1} role="row">
              {allCols.map((col) => {
                if (col < 0)
                  return (
                    <th
                      key={col}
                      aria-hidden="true"
                      style={{ padding: 0, border: 0 }}
                    />
                  )
                const selected = isInRange(range, row, col)
                const isFocus = focus.row === row && focus.col === col
                const text = cellText(row, col)
                const isLabel = col === LABEL_COL
                const mono = key === 'number' || key === 'contractId'
                return (
                  <th
                    key={col}
                    {...selectionProps(row, col, selected, isFocus)}
                    aria-colindex={col + 1}
                    role={isLabel ? 'rowheader' : 'columnheader'}
                    scope={isLabel ? 'row' : 'col'}
                    style={{
                      ...TABLE_HEADER_CELL_STYLE,
                      // 選択の面 (backgroundImage) と混ぜるので、 地は shorthand でなく backgroundColor で持つ
                      background: undefined,
                      backgroundColor: TABLE_HEADER_BG,
                      ...cellLines(headerRowCount, row, col),
                      ...(isLabel
                        ? {
                            position: 'sticky',
                            left: 0,
                            zIndex: 3,
                            textAlign: 'left',
                          }
                        : {
                            textAlign: key === 'total' ? 'right' : 'left',
                            fontWeight: 500,
                            fontVariantNumeric: 'tabular-nums',
                            fontFamily: mono ? 'monospace' : undefined,
                          }),
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      ...selectionStyle(row, col, selected, isFocus),
                    }}
                    title={isLabel ? undefined : text}
                  >
                    {text}
                  </th>
                )
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {rowStart > 0 && (
            <tr aria-hidden="true">
              <td
                colSpan={allCols.length}
                style={{
                  height: rowStart * viewport.rowHeight,
                  padding: 0,
                  border: 0,
                }}
              />
            </tr>
          )}
          {Array.from({ length: rowEnd - rowStart }, (_, index) => {
            const slot = index + rowStart
            const row = slot + headerRowCount
            return (
              <tr key={slot} aria-rowindex={row + 1} data-slot-row role="row">
                {allCols.map((col) => {
                  if (col < 0)
                    return (
                      <td
                        key={col}
                        aria-hidden="true"
                        style={{ padding: 0, border: 0 }}
                      />
                    )
                  const selected = isInRange(range, row, col)
                  const isFocus = focus.row === row && focus.col === col
                  const isCellEditing =
                    editing?.row === row && editing.col === col
                  const point =
                    col === LABEL_COL
                      ? undefined
                      : points[col - FIRST_POINT_COL]
                  const value = cellText(row, col)
                  // 編集中でないセルは 3 桁区切りで見せる (コピー・入力は生の数字のまま)
                  const shown = isCellEditing ? value : groupedText(value, col)
                  const invalid =
                    col !== LABEL_COL &&
                    value.trim() !== '' &&
                    parseSlotValue(value) === undefined
                  const blank =
                    point !== undefined &&
                    value === '' &&
                    isBlankSource(slotAt(point, slot)?.source)
                  return (
                    <td
                      key={col}
                      {...selectionProps(row, col, selected, isFocus)}
                      aria-colindex={col + 1}
                      aria-invalid={invalid || undefined}
                      aria-label={
                        point === undefined
                          ? undefined
                          : t(`${NS}.cellAria`, {
                              time: rowTimeLabel(slot),
                              number: point.supplyPointSpecificNumber,
                            })
                      }
                      data-blank={blank || undefined}
                      onDoubleClick={() => {
                        grid.startEdit(row, col)
                      }}
                      role="gridcell"
                      style={{
                        ...TABLE_BODY_CELL_STYLE,
                        position: 'relative',
                        ...cellLines(headerRowCount, row, col),
                        ...(col === LABEL_COL
                          ? {
                              position: 'sticky',
                              left: 0,
                              zIndex: 1,
                              backgroundColor: COLORS.timeBg,
                              fontWeight: 600,
                            }
                          : {
                              textAlign: 'right',
                              fontVariantNumeric: 'tabular-nums',
                            }),
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        color: invalid ? COLORS.invalidText : undefined,
                        ...(blank ? { backgroundColor: COLORS.blank } : {}),
                        ...selectionStyle(row, col, selected, isFocus),
                        ...(invalid
                          ? {
                              outline: `1px solid ${COLORS.invalidLine}`,
                              outlineOffset: -2,
                            }
                          : {}),
                      }}
                      title={
                        point === undefined
                          ? undefined
                          : cellHint(point, slot, value)
                      }
                    >
                      {isCellEditing ? (
                        <Box position="absolute" inset="0">
                          <Input
                            ref={(el) => {
                              el?.focus()
                            }}
                            textAlign="right"
                            aria-label={t(`${NS}.cellEditAria`, {
                              time: rowTimeLabel(slot),
                              number: point?.supplyPointSpecificNumber ?? '',
                            })}
                            inputMode="numeric"
                            {...grid.editInputHandlers(row, col)}
                            size="sm"
                            value={editing?.draft ?? ''}
                          />
                        </Box>
                      ) : (
                        shown
                      )}
                    </td>
                  )
                })}
              </tr>
            )
          })}
          {rowEnd < slotRowCount && (
            <tr aria-hidden="true">
              <td
                colSpan={allCols.length}
                style={{
                  height: (slotRowCount - rowEnd) * viewport.rowHeight,
                  padding: 0,
                  border: 0,
                }}
              />
            </tr>
          )}
        </tbody>
      </table>
    </Box>
  )
}
